import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable, ReportColumn } from '@/components/ReportTable';
import { apiFetch } from '@/lib/api';
import { formatCents, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import DateRangePresets, {
  type Preset,
  boundsFor,
  rangeReady,
} from '@/components/DateRangePresets';

// ─── Types ─────────────────────────────────────────────────────────────────────

type RefKind = 'estimate' | 'purchase-return' | 'purchase-order' | 'sales-return' | null;

interface CashStatementRow {
  date: string;
  type: string;
  reference: string;
  party: string;
  cash_in: number;
  cash_out: number;
  balance: number;
  ref_kind: RefKind;
  ref_id: number | null;
}

interface CashStatementData {
  from: string;
  to: string;
  opening_balance: number;
  rows: CashStatementRow[];
  total_in: number;
  total_out: number;
  closing_balance: number;
}

interface CashStatementResponse {
  data: CashStatementData;
}

// ─── Sort options ────────────────────────────────────────────────────────────────

const SORTS: { value: 'asc' | 'desc'; label: string }[] = [
  { value: 'desc', label: 'Newest first' },
  { value: 'asc', label: 'Oldest first' },
];

// ─── Reference → detail-route base mapping ──────────────────────────────────────

const REF_ROUTE_BASE: Record<Exclude<RefKind, null>, string> = {
  estimate: '/estimates/',
  'purchase-return': '/purchase-returns/',
  'purchase-order': '/purchase-orders/',
  'sales-return': '/returns/',
};

// ─── Columns ───────────────────────────────────────────────────────────────────

const COLUMNS: ReportColumn[] = [
  {
    key: 'date',
    header: 'Date',
    render: (value) => (value ? formatDate(String(value)) : '—'),
  },
  { key: 'type', header: 'Type' },
  {
    key: 'reference',
    header: 'Reference',
    render: (value, row) => {
      const r = row as unknown as CashStatementRow;
      const text = value == null ? '—' : String(value);
      if (r.ref_kind && r.ref_id != null) {
        return (
          <Link
            to={`${REF_ROUTE_BASE[r.ref_kind]}${r.ref_id}`}
            className="text-primary hover:underline underline-offset-4"
          >
            {text}
          </Link>
        );
      }
      return text;
    },
  },
  { key: 'party', header: 'Party' },
  { key: 'cash_in', header: 'Cash In', money: true },
  { key: 'cash_out', header: 'Cash Out', money: true },
  { key: 'balance', header: 'Balance', money: true },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function CashStatementReport() {
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');

  const { from, to } = useMemo(
    () => boundsFor(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'cash-statement', from, to, sort],
    queryFn: () =>
      apiFetch<CashStatementResponse>(
        `/api/reports/cash-statement?from=${from}&to=${to}&sort=${sort}`,
      ),
    enabled: rangeReady(preset, customFrom, customTo),
  });

  const report = data?.data;
  const rows = (report?.rows ?? []) as unknown as Record<string, unknown>[];

  // Empty `from` (the "All" preset) means all-time — read it sensibly in the header
  // instead of showing a blank start date.
  const rangeLabel = from ? `${formatDate(from)} – ${formatDate(to)}` : 'All time';

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Cash Statement</h1>
        <ExportButtons endpoint="/api/reports/cash-statement" params={{ from, to, sort }} pdf />
      </div>

      {/* Date-range presets */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <DateRangePresets
          preset={preset}
          onPresetChange={setPreset}
          customFrom={customFrom}
          onCustomFromChange={setCustomFrom}
          customTo={customTo}
          onCustomToChange={setCustomTo}
          idPrefix="cs"
        />

        {/* Sort order */}
        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 ml-auto">
          {SORTS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setSort(s.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                sort === s.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active range */}
      <p className="text-sm text-muted-foreground mb-6">{rangeLabel}</p>

      {/* Loading / error */}
      {isLoading && <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Opening Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold tabular-nums">
                  {formatCents(report.opening_balance)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total In
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold tabular-nums text-green-600">
                  {formatCents(report.total_in)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Out
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold tabular-nums text-red-600">
                  {formatCents(report.total_out)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Closing Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p
                  className={`text-2xl font-bold tabular-nums ${
                    report.closing_balance >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatCents(report.closing_balance)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Ledger */}
          <ReportTable columns={COLUMNS} rows={rows} />
        </>
      )}
    </div>
  );
}
